import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, CATEGORY_LABELS, GENDER_LABELS, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Trash2, Upload, Image as ImageIcon, Clock, Check, X } from "lucide-react";

const CATS = ["trendy", "traditional", "artisanal", "formal", "athleisure", "eco_friendly", "occasion_wear"];
const GENDERS = ["men", "women", "unisex"];

const emptyForm = {
    brand_name: "", tagline: "", description: "", website_url: "",
    instagram: "", logo_url: "", banner_url: "",
    gender: [], categories: [], city: "", region: "",
    founded_year: "", founder_name: "", price_range: "", craft_story: "",
    preview_images: [], gallery_images: [],
};

export default function StartupDashboard() {
    const { user, ready } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState("profile");
    const [form, setForm] = useState(emptyForm);
    const [status, setStatus] = useState(null); // pending | approved | rejected | null (no application yet)
    const [regions, setRegions] = useState([]);
    const [saving, setSaving] = useState(false);

    const [drops, setDrops] = useState([]);
    const [analytics, setAnalytics] = useState(null);
    const [analyticsRange, setAnalyticsRange] = useState("all");
    const [dropForm, setDropForm] = useState({ title: "", description: "", image_url: "", link_url: "" });
    const [uploadingImg, setUploadingImg] = useState(false);

    useEffect(() => {
        if (!ready) return;
        if (!user || user.role !== "startup") { navigate("/login"); return; }
        api.get("/startups/mine").then((r) => {
            if (r.data) {
                setForm({ ...emptyForm, ...r.data, founded_year: r.data.founded_year || "" });
                setStatus(r.data.status);
            }
        });
        api.get("/drops/mine").then((r) => setDrops(r.data || []));
        api.get("/regions").then((r) => setRegions(r.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ready, user]);

    // Re-fetch analytics when range changes
    useEffect(() => {
        if (!ready || !user || user.role !== "startup") return;
        api.get("/analytics/mine", { params: { range: analyticsRange } })
            .then((r) => setAnalytics(r.data))
            .catch(() => {});
    }, [ready, user, analyticsRange]);

    const toggle = (key, val) => {
        setForm((f) => ({ ...f, [key]: f[key].includes(val) ? f[key].filter((v) => v !== val) : [...f[key], val] }));
    };

    const uploadImage = async (e, key) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const fd = new FormData();
        fd.append("file", file);
        try {
            const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
            if (key === "drop_image") {
                setDropForm((d) => ({ ...d, image_url: fullUrl }));
            } else if (key === "preview_images" || key === "gallery_images") {
                const limit = key === "preview_images" ? 3 : 10;
                setForm((f) => {
                    const list = [...(f[key] || [])];
                    if (list.length >= limit) {
                        toast.error(`Max ${limit} images allowed for this section`);
                        return f;
                    }
                    list.push(fullUrl);
                    return { ...f, [key]: list };
                });
            } else {
                setForm((f) => ({ ...f, [key]: fullUrl }));
            }
            toast.success("Image uploaded");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        }
        // Reset input so same file can be re-picked
        e.target.value = "";
    };

    const removeGalleryImage = (key, idx) => {
        setForm((f) => ({ ...f, [key]: (f[key] || []).filter((_, i) => i !== idx) }));
    };

    const saveProfile = async (e) => {
        e.preventDefault();
        if ((form.preview_images || []).length < 3) {
            toast.error("Please upload all 3 preview images before submitting");
            return;
        }
        setSaving(true);
        try {
            const payload = { ...form, founded_year: form.founded_year ? Number(form.founded_year) : null };
            const { data } = await api.post("/startups", payload);
            setStatus(data.status);
            toast.success(data.status === "approved" ? "Profile updated" : "Application submitted — we'll review shortly");
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally { setSaving(false); }
    };

    const submitDrop = async (e) => {
        e.preventDefault();
        if (!dropForm.title || !dropForm.image_url) return toast.error("Add a title and cover image");
        setUploadingImg(true);
        try {
            const fd = new FormData();
            fd.append("title", dropForm.title);
            fd.append("description", dropForm.description);
            fd.append("image_url", dropForm.image_url);
            fd.append("link_url", dropForm.link_url);
            await api.post("/drops", fd);
            toast.success("Drop posted");
            setDropForm({ title: "", description: "", image_url: "", link_url: "" });
            const { data } = await api.get("/drops/mine");
            setDrops(data || []);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally { setUploadingImg(false); }
    };

    const deleteDrop = async (id) => {
        try {
            await api.delete(`/drops/${id}`);
            setDrops((d) => d.filter((x) => x.id !== id));
            toast.success("Deleted");
        } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    };

    if (!ready) return <div className="min-h-screen flex items-center justify-center text-white/50">Loading...</div>;

    const statusBadge = () => {
        if (status === "approved") return <span data-testid="status-badge" className="font-mono-tag text-xs uppercase px-3 py-1.5 bg-brand-saffron text-black rounded-sm flex items-center gap-1 w-fit"><Check className="w-3 h-3" /> Live on Primates</span>;
        if (status === "rejected") return <span data-testid="status-badge" className="font-mono-tag text-xs uppercase px-3 py-1.5 bg-brand-magenta text-white rounded-sm flex items-center gap-1 w-fit"><X className="w-3 h-3" /> Application declined</span>;
        if (status === "pending") return <span data-testid="status-badge" className="font-mono-tag text-xs uppercase px-3 py-1.5 bg-brand-marigold text-black rounded-sm flex items-center gap-1 w-fit"><Clock className="w-3 h-3" /> Under review</span>;
        return <span data-testid="status-badge" className="font-mono-tag text-xs uppercase px-3 py-1.5 border border-white/20 text-white/70 rounded-sm w-fit">No application yet</span>;
    };

    return (
        <main className="max-w-[1200px] mx-auto px-4 md:px-10 py-10 md:py-14 min-h-screen">
            <p className="font-mono-tag text-xs uppercase text-brand-saffron mb-3">/ brand dashboard</p>
            <h1 data-testid="dashboard-title" className="font-display font-extrabold text-3xl sm:text-4xl md:text-6xl tracking-tighter uppercase mb-4">Your <span className="text-brand-saffron">drop zone.</span></h1>
            <div className="mb-10">{statusBadge()}</div>

            {status === "pending" && (
                <div className="mb-8 p-5 rounded-md border border-brand-marigold/30 bg-brand-marigold/5">
                    <p className="font-mono-tag text-xs uppercase text-brand-marigold mb-2">/ under review</p>
                    <p className="text-white/80 text-sm">Not every brand can list here. Our editors are reviewing your application — we'll get back within 3–5 days.</p>
                </div>
            )}

            <div className="flex gap-2 mb-10 border-b border-white/10 overflow-x-auto -mx-4 md:mx-0 px-4 md:px-0 scrollbar-hide">
                <button data-testid="dashboard-tab-profile" onClick={() => setTab("profile")} className={`shrink-0 py-3 px-5 font-mono-tag text-xs uppercase transition-colors ${tab === "profile" ? "text-brand-saffron border-b-2 border-brand-saffron" : "text-white/60 hover:text-white"}`}>{status ? "Brand Profile" : "Apply to List"}</button>
                <button data-testid="dashboard-tab-drops" onClick={() => setTab("drops")} className={`shrink-0 py-3 px-5 font-mono-tag text-xs uppercase transition-colors ${tab === "drops" ? "text-brand-saffron border-b-2 border-brand-saffron" : "text-white/60 hover:text-white"}`}>Drops / Campaigns</button>
                <button data-testid="dashboard-tab-analytics" onClick={() => setTab("analytics")} className={`shrink-0 py-3 px-5 font-mono-tag text-xs uppercase transition-colors ${tab === "analytics" ? "text-brand-saffron border-b-2 border-brand-saffron" : "text-white/60 hover:text-white"}`}>Analytics</button>
            </div>

            {tab === "profile" && (
                <form onSubmit={saveProfile} className="space-y-6 max-w-3xl">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label className="font-mono-tag text-xs uppercase text-white/70">Brand name *</Label>
                            <Input data-testid="dashboard-brand-name-input" required value={form.brand_name} onChange={(e) => setForm({ ...form, brand_name: e.target.value })} className="mt-2 bg-brand-ink border-white/15 h-12" />
                        </div>
                        <div>
                            <Label className="font-mono-tag text-xs uppercase text-white/70">City *</Label>
                            <Input data-testid="dashboard-city-input" required value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="mt-2 bg-brand-ink border-white/15 h-12" />
                        </div>
                    </div>
                    <div>
                        <Label className="font-mono-tag text-xs uppercase text-white/70">Tagline *</Label>
                        <Input data-testid="dashboard-tagline-input" required value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} className="mt-2 bg-brand-ink border-white/15 h-12" placeholder="One line that captures your brand" />
                    </div>
                    <div>
                        <Label className="font-mono-tag text-xs uppercase text-white/70">Brand description *</Label>
                        <Textarea data-testid="dashboard-description-input" required rows={4} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-2 bg-brand-ink border-white/15" placeholder="What do you make, who is it for, why now?" />
                    </div>
                    <div>
                        <Label className="font-mono-tag text-xs uppercase text-white/70">Craft / heritage story (if applicable)</Label>
                        <Textarea data-testid="dashboard-craft-story-input" rows={3} value={form.craft_story} onChange={(e) => setForm({ ...form, craft_story: e.target.value })} className="mt-2 bg-brand-ink border-white/15" placeholder="Working with artisans, using a specific technique, made in a specific region..." />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <Label className="font-mono-tag text-xs uppercase text-white/70">Founder name *</Label>
                            <Input data-testid="dashboard-founder-input" required value={form.founder_name} onChange={(e) => setForm({ ...form, founder_name: e.target.value })} className="mt-2 bg-brand-ink border-white/15 h-12" />
                        </div>
                        <div>
                            <Label className="font-mono-tag text-xs uppercase text-white/70">Founded year *</Label>
                            <Input data-testid="dashboard-founded-input" required type="number" min="2000" max="2026" value={form.founded_year} onChange={(e) => setForm({ ...form, founded_year: e.target.value })} className="mt-2 bg-brand-ink border-white/15 h-12" />
                        </div>
                        <div>
                            <Label className="font-mono-tag text-xs uppercase text-white/70">Price range</Label>
                            <Input data-testid="dashboard-price-input" value={form.price_range} onChange={(e) => setForm({ ...form, price_range: e.target.value })} className="mt-2 bg-brand-ink border-white/15 h-12" placeholder="₹500 – ₹5,000" />
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label className="font-mono-tag text-xs uppercase text-white/70">Website URL *</Label>
                            <Input data-testid="dashboard-website-input" required type="url" value={form.website_url} onChange={(e) => setForm({ ...form, website_url: e.target.value })} className="mt-2 bg-brand-ink border-white/15 h-12" placeholder="https://yourbrand.com" />
                        </div>
                        <div>
                            <Label className="font-mono-tag text-xs uppercase text-white/70">Instagram handle</Label>
                            <Input data-testid="dashboard-instagram-input" value={form.instagram} onChange={(e) => setForm({ ...form, instagram: e.target.value })} className="mt-2 bg-brand-ink border-white/15 h-12" placeholder="@yourbrand" />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <Label className="font-mono-tag text-xs uppercase text-white/70">Logo</Label>
                            <div className="mt-2 flex items-center gap-3">
                                {form.logo_url && <img src={form.logo_url} alt="logo" className="w-14 h-14 rounded object-cover border border-white/10" />}
                                <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-3 border border-white/15 rounded-md hover:border-brand-saffron transition-colors text-sm">
                                    <Upload className="w-4 h-4" /> Upload
                                    <input data-testid="dashboard-logo-upload" type="file" accept="image/*" hidden onChange={(e) => uploadImage(e, "logo_url")} />
                                </label>
                            </div>
                        </div>
                        <div>
                            <Label className="font-mono-tag text-xs uppercase text-white/70">Banner</Label>
                            <div className="mt-2 flex items-center gap-3">
                                {form.banner_url && <img src={form.banner_url} alt="banner" className="w-24 h-14 rounded object-cover border border-white/10" />}
                                <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-3 border border-white/15 rounded-md hover:border-brand-saffron transition-colors text-sm">
                                    <Upload className="w-4 h-4" /> Upload
                                    <input data-testid="dashboard-banner-upload" type="file" accept="image/*" hidden onChange={(e) => uploadImage(e, "banner_url")} />
                                </label>
                            </div>
                        </div>
                    </div>

                    <div>
                        <Label className="font-mono-tag text-xs uppercase text-white/70">Preview images — required, exactly 3 <span className="text-brand-magenta">*</span></Label>
                        <div className="mt-3 flex flex-wrap gap-3">
                            {(form.preview_images || []).map((src, i) => (
                                <div key={i} data-testid={`preview-image-${i}`} className="relative w-24 h-32 rounded-md overflow-hidden border border-white/10 group">
                                    <img src={src} alt="" className="w-full h-full object-cover" />
                                    <button type="button" data-testid={`remove-preview-${i}`} onClick={() => removeGalleryImage("preview_images", i)}
                                        className="absolute top-1 right-1 p-1 bg-black/70 hover:bg-brand-magenta rounded-full transition-colors">
                                        <Trash2 className="w-3 h-3 text-white" />
                                    </button>
                                </div>
                            ))}
                            {(form.preview_images || []).length < 3 && (
                                <label className={`cursor-pointer w-24 h-32 rounded-md border-2 border-dashed flex flex-col items-center justify-center transition-colors ${
                                    (form.preview_images || []).length === 0 ? "border-brand-magenta/70 text-brand-magenta hover:border-brand-magenta" : "border-white/20 text-white/60 hover:border-brand-saffron hover:text-brand-saffron"
                                }`}>
                                    <Upload className="w-4 h-4 mb-1" />
                                    <span className="font-mono-tag text-[10px] uppercase">Add {3 - (form.preview_images || []).length} more</span>
                                    <input data-testid="dashboard-preview-upload" type="file" accept="image/*" hidden onChange={(e) => uploadImage(e, "preview_images")} />
                                </label>
                            )}
                        </div>
                        <p className={`mt-2 text-xs ${(form.preview_images || []).length < 3 ? "text-brand-magenta" : "text-white/40"}`}>
                            {(form.preview_images || []).length < 3
                                ? `You need ${3 - (form.preview_images || []).length} more image${3 - (form.preview_images || []).length === 1 ? "" : "s"} — 3 previews are mandatory before submitting.`
                                : "All 3 previews uploaded. They auto-cycle on your card in Discover."}
                        </p>
                    </div>

                    <div>
                        <Label className="font-mono-tag text-xs uppercase text-white/70">Gallery photos — shown when your tab is opened (up to 10)</Label>
                        <div className="mt-3 grid grid-cols-3 md:grid-cols-5 gap-2">
                            {(form.gallery_images || []).map((src, i) => (
                                <div key={i} data-testid={`gallery-image-${i}`} className="relative aspect-square rounded-md overflow-hidden border border-white/10 group">
                                    <img src={src} alt="" className="w-full h-full object-cover" />
                                    <button type="button" data-testid={`remove-gallery-${i}`} onClick={() => removeGalleryImage("gallery_images", i)}
                                        className="absolute top-1 right-1 p-1 bg-black/70 hover:bg-brand-magenta rounded-full transition-colors">
                                        <Trash2 className="w-3 h-3 text-white" />
                                    </button>
                                </div>
                            ))}
                            {(form.gallery_images || []).length < 10 && (
                                <label className="cursor-pointer aspect-square rounded-md border-2 border-dashed border-white/20 hover:border-brand-saffron flex flex-col items-center justify-center text-white/60 hover:text-brand-saffron transition-colors">
                                    <Upload className="w-4 h-4 mb-1" />
                                    <span className="font-mono-tag text-[10px] uppercase">{10 - (form.gallery_images || []).length} left</span>
                                    <input data-testid="dashboard-gallery-upload" type="file" accept="image/*" hidden onChange={(e) => uploadImage(e, "gallery_images")} />
                                </label>
                            )}
                        </div>
                        <p className="mt-2 text-white/40 text-xs">Shoppers see these on your detail page.</p>
                    </div>

                    <div>
                        <Label className="font-mono-tag text-xs uppercase text-white/70">Region (for traditional listings)</Label>
                        <div className="mt-3 flex flex-wrap gap-2">
                            <button type="button" onClick={() => setForm({ ...form, region: "" })}
                                data-testid="dashboard-region-none"
                                className={`px-4 py-2 rounded-full font-mono-tag text-xs uppercase border transition-colors ${form.region === "" ? "bg-brand-marigold text-black border-brand-marigold" : "bg-transparent border-white/20 text-white hover:border-brand-marigold"}`}>
                                — None —
                            </button>
                            {regions.map((r) => (
                                <button type="button" key={r.id} data-testid={`dashboard-region-${r.id}`} onClick={() => setForm({ ...form, region: r.id })}
                                    className={`px-4 py-2 rounded-full font-mono-tag text-xs uppercase border transition-colors ${form.region === r.id ? "bg-brand-marigold text-black border-brand-marigold" : "bg-transparent border-white/20 text-white hover:border-brand-marigold"}`}>
                                    {r.name}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div>
                        <Label className="font-mono-tag text-xs uppercase text-white/70">Gender *</Label>
                        <div className="mt-3 flex flex-wrap gap-2">
                            {GENDERS.map((g) => (
                                <button type="button" key={g} data-testid={`dashboard-gender-${g}`} onClick={() => toggle("gender", g)}
                                    className={`px-4 py-2 rounded-full font-mono-tag text-xs uppercase border transition-colors ${form.gender.includes(g) ? "bg-brand-saffron text-black border-brand-saffron" : "bg-transparent border-white/20 text-white hover:border-brand-saffron"}`}>
                                    {GENDER_LABELS[g]}
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <Label className="font-mono-tag text-xs uppercase text-white/70">Categories *</Label>
                        <div className="mt-3 flex flex-wrap gap-2">
                            {CATS.map((c) => (
                                <button type="button" key={c} data-testid={`dashboard-category-${c}`} onClick={() => toggle("categories", c)}
                                    className={`px-4 py-2 rounded-full font-mono-tag text-xs uppercase border transition-colors ${form.categories.includes(c) ? "bg-brand-saffron text-black border-brand-saffron" : "bg-transparent border-white/20 text-white hover:border-brand-saffron"}`}>
                                    {CATEGORY_LABELS[c]}
                                </button>
                            ))}
                        </div>
                    </div>

                    <Button data-testid="dashboard-save-btn" type="submit" disabled={saving || (form.preview_images || []).length < 3} className="bg-brand-saffron text-black hover:bg-brand-saffron/90 font-semibold h-12 px-8 rounded-md disabled:opacity-50 disabled:cursor-not-allowed">
                        {saving ? "Saving..." : status ? "Update profile" : "Submit application"}
                    </Button>
                </form>
            )}

            {tab === "drops" && (
                <div className="space-y-10 max-w-3xl">
                    {status !== "approved" ? (
                        <div className="p-8 rounded-md border border-white/10 grain-card text-center">
                            <p className="font-display text-2xl mb-2">Drops unlock after approval.</p>
                            <p className="text-white/60 text-sm">Complete your brand profile — once our editors approve it, you can post campaigns here.</p>
                        </div>
                    ) : (
                        <form onSubmit={submitDrop} className="grain-card p-6 rounded-md space-y-4">
                            <p className="font-display font-bold text-2xl tracking-tight flex items-center gap-2"><ImageIcon className="w-5 h-5 text-brand-saffron" /> Post a new drop</p>
                            <div>
                                <Label className="font-mono-tag text-xs uppercase text-white/70">Campaign title *</Label>
                                <Input data-testid="dashboard-drop-title" required value={dropForm.title} onChange={(e) => setDropForm({ ...dropForm, title: e.target.value })} className="mt-2 bg-brand-ink border-white/15 h-12" placeholder="Winter '26 Lookbook" />
                            </div>
                            <div>
                                <Label className="font-mono-tag text-xs uppercase text-white/70">Description</Label>
                                <Textarea data-testid="dashboard-drop-description" rows={3} value={dropForm.description} onChange={(e) => setDropForm({ ...dropForm, description: e.target.value })} className="mt-2 bg-brand-ink border-white/15" />
                            </div>
                            <div>
                                <Label className="font-mono-tag text-xs uppercase text-white/70">Cover image *</Label>
                                <div className="mt-2 flex items-center gap-3">
                                    {dropForm.image_url && <img src={dropForm.image_url} alt="cover" className="w-24 h-24 rounded object-cover border border-white/10" />}
                                    <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-3 border border-white/15 rounded-md hover:border-brand-saffron transition-colors text-sm">
                                        <Upload className="w-4 h-4" /> Upload cover
                                        <input data-testid="dashboard-drop-image-upload" type="file" accept="image/*" hidden onChange={(e) => uploadImage(e, "drop_image")} />
                                    </label>
                                </div>
                            </div>
                            <div>
                                <Label className="font-mono-tag text-xs uppercase text-white/70">Shop link (optional — defaults to your website)</Label>
                                <Input data-testid="dashboard-drop-link" type="url" value={dropForm.link_url} onChange={(e) => setDropForm({ ...dropForm, link_url: e.target.value })} className="mt-2 bg-brand-ink border-white/15 h-12" placeholder="https://yourbrand.com/winter-26" />
                            </div>
                            <Button data-testid="dashboard-post-drop-btn" type="submit" disabled={uploadingImg} className="bg-brand-saffron text-black hover:bg-brand-saffron/90 font-semibold h-12 px-8 rounded-md">
                                {uploadingImg ? "Posting..." : "Post drop"}
                            </Button>
                        </form>
                    )}

                    <div>
                        <p className="font-mono-tag text-xs uppercase text-white/50 mb-4">/ your drops ({drops.length})</p>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {drops.map((d) => (
                                <div key={d.id} className="relative aspect-[4/5] rounded-md overflow-hidden border border-white/10 bg-brand-ink group">
                                    <img src={d.image_url} alt={d.title} className="w-full h-full object-cover" />
                                    <button data-testid={`dashboard-delete-drop-${d.id}`} onClick={() => deleteDrop(d.id)} className="absolute top-2 right-2 p-2 bg-black/70 hover:bg-brand-magenta rounded-full transition-colors opacity-0 group-hover:opacity-100">
                                        <Trash2 className="w-3 h-3 text-white" />
                                    </button>
                                    <p className="absolute bottom-0 left-0 right-0 p-2 text-xs text-white bg-gradient-to-t from-black to-transparent line-clamp-2 font-display font-bold">{d.title}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            )}

            {tab === "analytics" && (
                <div className="max-w-4xl space-y-8">
                    {!analytics || !analytics.startup ? (
                        <div className="p-8 rounded-md border border-white/10 grain-card text-center">
                            <p className="font-display text-2xl mb-2">No stats yet.</p>
                            <p className="text-white/60 text-sm">Set up your brand profile — once you're approved and shoppers start clicking through, this fills up.</p>
                        </div>
                    ) : (
                        <>
                            <div className="flex gap-2 flex-wrap">
                                {[
                                    { id: "7d", label: "Last 7 days" },
                                    { id: "30d", label: "Last 30 days" },
                                    { id: "all", label: "All time" },
                                ].map((r) => (
                                    <button key={r.id} data-testid={`analytics-range-${r.id}`}
                                        onClick={() => setAnalyticsRange(r.id)}
                                        className={`px-4 py-2 rounded-full font-mono-tag text-xs uppercase border transition-colors ${
                                            analyticsRange === r.id ? "bg-brand-saffron text-black border-brand-saffron" : "bg-transparent text-white border-white/20 hover:border-brand-saffron"
                                        }`}
                                    >{r.label}</button>
                                ))}
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                                {[
                                    { key: "profile_views", label: "Profile Views", color: "text-brand-saffron" },
                                    { key: "visit_clicks", label: "Website Clicks", color: "text-brand-marigold" },
                                    { key: "followers", label: "Followers", color: "text-brand-magenta" },
                                    { key: "drop_count", label: "Drops Posted", color: "text-brand-teal" },
                                    { key: "drop_views", label: "Drop Views", color: "text-brand-saffron" },
                                    { key: "drop_clicks", label: "Drop Clicks", color: "text-brand-marigold" },
                                ].map((k) => (
                                    <div key={k.key} data-testid={`analytics-${k.key}`} className="grain-card p-6 rounded-md">
                                        <p className="font-mono-tag text-[10px] uppercase text-white/50 mb-2">/ {k.label}</p>
                                        <p className={`font-display font-extrabold text-4xl tracking-tighter ${k.color}`}>{analytics.totals[k.key] || 0}</p>
                                    </div>
                                ))}
                            </div>

                            {/* Sparkline chart */}
                            {analytics.series?.length > 0 && (
                                <div className="grain-card p-6 rounded-md">
                                    <p className="font-mono-tag text-xs uppercase text-white/50 mb-4">/ daily activity ({analyticsRange === "all" ? "last 30 days" : analyticsRange === "7d" ? "last 7 days" : "last 30 days"})</p>
                                    <SparklineChart series={analytics.series} />
                                </div>
                            )}

                            {analytics.drops?.length > 0 && (
                                <div>
                                    <p className="font-mono-tag text-xs uppercase text-white/50 mb-4">/ per-drop performance</p>
                                    <div className="grain-card rounded-md overflow-hidden">
                                        <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-white/10 font-mono-tag text-[10px] uppercase text-white/50">
                                            <div className="col-span-6">Drop</div>
                                            <div className="col-span-3 text-right">Views</div>
                                            <div className="col-span-3 text-right">Clicks</div>
                                        </div>
                                        {analytics.drops.map((d) => (
                                            <div key={d.id} data-testid={`analytics-drop-${d.id}`} className="grid grid-cols-12 gap-4 px-5 py-4 border-b border-white/5 last:border-b-0 items-center">
                                                <div className="col-span-6 flex items-center gap-3 min-w-0">
                                                    <img src={d.image_url} alt="" className="w-10 h-10 rounded object-cover shrink-0" />
                                                    <p className="text-sm font-medium truncate">{d.title}</p>
                                                </div>
                                                <div className="col-span-3 text-right font-mono-tag text-sm text-brand-saffron">{d.views}</div>
                                                <div className="col-span-3 text-right font-mono-tag text-sm text-brand-marigold">{d.clicks}</div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </main>
    );
}

function SparklineChart({ series }) {
    const width = 600, height = 140, padding = { l: 8, r: 8, t: 12, b: 22 };
    const iw = width - padding.l - padding.r;
    const ih = height - padding.t - padding.b;
    const keys = [
        { key: "profile_views", color: "#FF6B1A" },
        { key: "visit_clicks", color: "#FFB800" },
        { key: "drop_views", color: "#FF1F6E" },
        { key: "drop_clicks", color: "#0F5257" },
    ];
    const maxV = Math.max(1, ...series.flatMap((d) => keys.map((k) => d[k.key] || 0)));
    const n = series.length;
    const x = (i) => padding.l + (n > 1 ? (i / (n - 1)) * iw : iw / 2);
    const y = (v) => padding.t + ih - (v / maxV) * ih;

    return (
        <div className="w-full overflow-x-auto">
            <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-auto" preserveAspectRatio="none">
                {/* grid */}
                {[0.25, 0.5, 0.75, 1].map((f) => (
                    <line key={f} x1={padding.l} y1={padding.t + ih * f} x2={width - padding.r} y2={padding.t + ih * f}
                        stroke="rgba(255,255,255,0.06)" strokeWidth={0.5} />
                ))}
                {keys.map((k) => {
                    const pts = series.map((d, i) => `${x(i)},${y(d[k.key] || 0)}`).join(" ");
                    return (
                        <g key={k.key}>
                            <polyline points={pts} fill="none" stroke={k.color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" opacity={0.9} />
                            {series.map((d, i) => (
                                <circle key={i} cx={x(i)} cy={y(d[k.key] || 0)} r={2} fill={k.color} opacity={0.8} />
                            ))}
                        </g>
                    );
                })}
                {/* x-axis labels — show first, middle, last */}
                {[0, Math.floor(n / 2), n - 1].filter((v, i, a) => a.indexOf(v) === i).map((i) => (
                    <text key={i} x={x(i)} y={height - 4} textAnchor="middle"
                        style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 8, fill: "rgba(255,255,255,0.4)" }}>
                        {series[i]?.date?.slice(5) || ""}
                    </text>
                ))}
            </svg>
            <div className="mt-3 flex flex-wrap gap-4 font-mono-tag text-[10px] uppercase text-white/60">
                {keys.map((k) => (
                    <span key={k.key} className="flex items-center gap-1.5">
                        <span className="w-2 h-2 rounded-full" style={{ background: k.color }} />
                        {k.key.replace("_", " ")}
                    </span>
                ))}
            </div>
        </div>
    );
}
